/**
 * Recently Viewed Products
 *
 * Pixel-perfect sibling of <product-recommendations>, but populated from the
 * customer's locally-stored browsing history instead of Shopify's recommendation
 * engine. Cards are the exact same `item-product` snippet, fetched per handle via
 * an alternate product template (`?view=recently-viewed-card`) and assembled into
 * the same Swiper markup.
 *
 * Isolated from product-recommendations.js: distinct custom element, distinct
 * swiper/pagination selectors and a distinct swatch-binding flag, so the two
 * sections can coexist on the same PDP without colliding.
 *
 * Each history entry is `{handle, variantId}` — the variant that was active on
 * the product page right before the customer navigated away. This lets the card
 * request that exact variant (`?variant=<id>`), which Shopify resolves natively
 * into `product.selected_variant` regardless of which template renders it — so
 * `item-product.liquid` picks up that variant's own image instead of the
 * product's default featured image. See getActiveVariantId() for how the
 * variant id is determined; older entries (plain handle strings, no variant)
 * are migrated on read.
 */
class RecentlyViewedProducts extends HTMLElement {

  static STORAGE_KEY = 'recentlyViewedProducts';
  static STORAGE_LIMIT = 100;

  variantsCache = new Map();

  connectedCallback() {
    // Always record the current product (even on the customer's very first PDP,
    // so it shows up on the next product they visit).
    this.recordCurrentProduct();

    // Re-resolve the active variant right before the customer leaves this page,
    // so a colour/size change made after the initial record is still captured.
    // visibilitychange (tab hidden) + pagehide cover both same-tab navigation
    // and tab close/switch; neither blocks navigation like beforeunload would.
    const captureOnLeave = () => this.updateActiveVariantForCurrentProduct();
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') captureOnLeave();
    });
    window.addEventListener('pagehide', captureOnLeave);

    // The section starts hidden (display:none) and reveals itself only once its
    // cards have loaded. We deliberately do NOT lazy-load via IntersectionObserver
    // here: a display:none element never intersects the viewport, so the observer
    // would never fire. Trigger on DOM ready instead — that also guarantees our
    // child `.swiper-wrapper` has been parsed before we query it.
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.loadRecentlyViewed(), { once: true });
    } else {
      this.loadRecentlyViewed();
    }
  }

  getStoredEntries() {
    try {
      const raw = localStorage.getItem(this.constructor.STORAGE_KEY);
      const parsed = JSON.parse(raw);
      if (!Array.isArray(parsed)) return [];

      // Migrate legacy entries (plain handle strings) to the {handle, variantId} shape.
      return parsed
        .map((entry) => {
          if (typeof entry === 'string') {
            return entry ? { handle: entry, variantId: null } : null;
          }
          if (entry && typeof entry === 'object' && entry.handle) {
            return { handle: String(entry.handle), variantId: entry.variantId ? String(entry.variantId) : null };
          }
          return null;
        })
        .filter(Boolean);
    } catch (e) {
      return [];
    }
  }

  setStoredEntries(entries) {
    try {
      localStorage.setItem(this.constructor.STORAGE_KEY, JSON.stringify(entries));
    } catch (e) {
      /* storage unavailable / quota — fail silently */
    }
  }

  /**
   * Determine the variant the customer currently has selected on THIS page.
   *
   * Colour swatches on the PDP (`.option-color`) update their `active` class
   * instantly on click but do NOT update the native `#productSelect` value —
   * only picking a size does. So the most accurate reading combines the two:
   * the actively-highlighted colour swatch + the size baked into whichever
   * variant `#productSelect` currently points at (via the page's own
   * `window.allVars` variant list, set in snippets/product-js.liquid).
   *
   * Both lookups are scoped to #ProductDetails (the main product form's
   * container) because `#productSelect` / `.option-color` ids and classes are
   * reused inside the shared quick-add popup markup for OTHER products.
   *
   * Falls back to the select's raw value, then the `?variant=` URL param, for
   * templates/products without colour swatches.
   */
  getActiveVariantId() {
    const scope = document.getElementById('ProductDetails');
    const select = scope ? scope.querySelector('#productSelect') : null;

    if (scope && Array.isArray(window.allVars) && window.allVars.length) {
      const activeColorEl = scope.querySelector('.option-color.active[data-val]');

      if (activeColorEl) {
        const activeColor = activeColorEl.dataset.val.trim().toLowerCase();

        let currentSize = null;
        if (select && select.value) {
          const selectedVariant = window.allVars.find((v) => String(v.id) === String(select.value));
          currentSize = selectedVariant ? selectedVariant.option1 : null;
        }

        const colorMatches = window.allVars.filter(
          (v) => (v.option2 || '').trim().toLowerCase() === activeColor
        );

        const exact = colorMatches.find((v) => v.option1 === currentSize) || colorMatches[0];
        if (exact && exact.id) return String(exact.id);
      }
    }

    // Single-option / no-colour-swatch products: the native select IS the
    // source of truth for the exact variant.
    if (select && select.value && /^\d+$/.test(select.value)) {
      return select.value;
    }

    // Last resort: whatever the URL says (correct for shared/deep links, and
    // for the subset of interactions that do sync history via ProductOptionSelectors).
    const fromUrl = new URLSearchParams(window.location.search).get('variant');
    if (fromUrl && /^\d+$/.test(fromUrl)) return fromUrl;

    return null;
  }

  recordCurrentProduct() {
    const handle = (this.dataset.productHandle || '').trim();
    if (!handle) return;

    let entries = this.getStoredEntries().filter((e) => e.handle !== handle);
    entries.unshift({ handle, variantId: this.getActiveVariantId() });

    if (entries.length > this.constructor.STORAGE_LIMIT) {
      entries = entries.slice(0, this.constructor.STORAGE_LIMIT);
    }

    this.setStoredEntries(entries);
  }

  // Patches the variant on the already-recorded entry for this page's product,
  // without reordering the list — "recently viewed" order reflects visit order,
  // only the remembered variant should reflect the customer's latest choice.
  updateActiveVariantForCurrentProduct() {
    const handle = (this.dataset.productHandle || '').trim();
    if (!handle) return;

    const variantId = this.getActiveVariantId();
    if (!variantId) return;

    const entries = this.getStoredEntries();
    const entry = entries.find((e) => e.handle === handle);
    if (entry && entry.variantId !== variantId) {
      entry.variantId = variantId;
      this.setStoredEntries(entries);
    }
  }

  getViewedEntriesToShow() {
    const current = (this.dataset.productHandle || '').trim();
    const limit = parseInt(this.dataset.productsToShow, 10);
    const safeLimit = Number.isFinite(limit) && limit > 0 ? limit : 12;

    return this.getStoredEntries()
      .filter((e) => e.handle && e.handle !== current)
      .slice(0, safeLimit);
  }

  async loadRecentlyViewed() {

    // Guard against re-entry (e.g. the element being reconnected, or a section
    // reload in the theme editor) so cards are never appended twice.
    if (this.__rvLoaded) return;
    this.__rvLoaded = true;

    try {

      const entries = this.getViewedEntriesToShow();
      if (!entries.length) return;

      const wrapper = this.querySelector('.swiper-wrapper');
      if (!wrapper) return;

      const fetched = await Promise.all(entries.map((entry) => this.fetchCard(entry)));

      const items = [];
      const slides = [];

      fetched.forEach((card) => {
        if (!card) return;
        items.push(card);
        const slide = document.createElement('div');
        slide.className = 'swiper-slide';
        slide.appendChild(card);
        slides.push(slide);
      });

      if (!items.length) return;

      // The item-product card renders its own featured image and (when enabled)
      // colour swatches server-side. Only reach for the Storefront API if a card
      // is missing its main image or carries swatches that need availability
      // filtering — in the default configuration this is skipped entirely.
      const needsProcessing = items.some(
        (item) => !item.querySelector('#item-product-img img') || item.querySelector('.product-card-color-option')
      );
      if (needsProcessing) {
        // Hydration failures (e.g. a Storefront API error) must not hide the
        // whole section — render the cards regardless.
        try {
          await this.processItems(items);
        } catch (e) {
          console.error('Recently viewed: card hydration failed', e);
        }
      }

      slides.forEach((slide) => wrapper.appendChild(slide));

      // Reveal before initialising Swiper so slide widths are measured correctly
      // (Swiper miscalculates dimensions inside a display:none container).
      this.reveal();

      this.initRecommendationCardPrices(this);
      this.initSwiper();
      this.initCardColorSwipers(this);
      this.bindCardColorSwatchEvents();

    } catch (e) {
      console.error('Recently viewed error:', e);
    }
  }

  async fetchCard(entry) {
    try {
      const params = new URLSearchParams({ view: 'recently-viewed-card' });
      if (entry.variantId) {
        params.set('variant', entry.variantId);
      }

      const response = await fetch(`/products/${encodeURIComponent(entry.handle)}?${params.toString()}`);
      if (!response.ok) return null;

      const text = await response.text();

      // A working alternate template (layout none) returns only the card markup.
      // If we got a full document instead (e.g. ?view= fell back to the default
      // product template), bail out rather than scraping an unrelated card.
      if (/<html[\s>]/i.test(text)) return null;

      const html = document.createElement('div');
      html.innerHTML = text;

      const card = html.querySelector('.grid-item');

      // Products whose gallery uses the per-variant `custom.images` metafield get
      // `data-needs-metafield-fallback="true"` from item-product.liquid, which makes
      // product-card-grid.js re-fetch full variant data and repaint the card image —
      // but that repaint prioritises the PRODUCT's default colour (data-main-color),
      // not any particular variant. Stamping the variant we actually asked for here
      // (client-side only, no Liquid change, so every other card on the site is
      // unaffected) lets that repaint prefer OUR variant's colour instead.
      if (card && entry.variantId) {
        card.dataset.requestedVariantId = entry.variantId;
      }

      return card;
    } catch (e) {
      return null;
    }
  }

  reveal() {
    const bg = this.closest('.recently-viewed__bg');
    if (bg) {
      bg.style.display = '';
    }
  }

  async processItems(items) {

    const jobs = items.map(async (item) => {

      const container = item.querySelector('#item-product-img');
      const hasMainImage = !!(container && container.querySelector('img'));

      const rawId = item.id;
      if (!rawId) return;

      const numericId = rawId.replace(/\D+/g, '');
      const gid = `gid://shopify/Product/${numericId}`;

      const variants = await this.fetchAllVariants(gid);
      this.filterUnavailableCardSwatches(variants, item);

      if (!hasMainImage) {
        this.renderColors(variants, item);
      }

      this.hideImagePlaceholders(item);

    });

    await Promise.all(jobs);
  }

  hideImagePlaceholders(item) {
    if (!item) {
      return;
    }

    item.querySelectorAll('.item-product-img__placeholder').forEach((placeholder) => {
      placeholder.remove();
    });
  }

  async fetchAllVariants(id) {

    if (this.variantsCache.has(id)) {
      return this.variantsCache.get(id);
    }

    let all = [];
    let cursor = null;
    let hasNext = true;

    while (hasNext) {

      const query = `
        query ($id: ID!, $cursor: String) {
          node(id: $id) {
            ... on Product {
              variants(first: 250, after: $cursor) {
                edges {
                  node {
                    id
                    availableForSale
                    selectedOptions {
                      name
                      value
                    }
                    active: metafield(namespace: "custom", key: "active") {
                      value
                    }
                    images: metafield(namespace: "custom", key: "images") {
                      references(first: 20) {
                        edges {
                          node {
                            ... on MediaImage {
                              image { url }
                            }
                          }
                        }
                      }
                    }
                  }
                }
                pageInfo {
                  hasNextPage
                  endCursor
                }
              }
            }
          }
        }
      `;

      const res = await fetch(
        ((window.LIVE && window.LIVE.storefrontUrl) || `https://live-ecom.myshopify.com/api/2025-07/graphql.json`),
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Shopify-Storefront-Access-Token': ((window.LIVE && window.LIVE.storefrontToken) || 'ed761c0d0f68bc596e5e4dd4e29e6407')
          },
          body: JSON.stringify({ query, variables: { id, cursor } })
        }
      );

      const json = await res.json();

      const variants = json.data.node.variants;

      all.push(...variants.edges.map(e => e.node));

      hasNext = variants.pageInfo.hasNextPage;
      cursor = variants.pageInfo.endCursor;

    }

    this.variantsCache.set(id, all);

    return all;
  }

  filterUnavailableCardSwatches(allVariants, item) {
    if (!allVariants || !allVariants.length || !item) {
      return;
    }

    const isVariantVisibleByActiveMetafield = (variant) => {
      const activeRaw = variant?.active?.value;
      if (activeRaw === null || activeRaw === undefined || activeRaw === '') {
        return true;
      }

      const normalized = String(activeRaw).trim().toLowerCase();
      if (normalized === 'false' || normalized === '0') {
        return false;
      }

      return normalized === 'true' || normalized === '1';
    };

    const availableVisibleVariantIds = new Set(
      allVariants
        .filter((variant) => variant && variant.availableForSale && isVariantVisibleByActiveMetafield(variant))
        .map((variant) => {
          const gid = String(variant.id || '');
          return gid.split('/').pop();
        })
        .filter(Boolean)
    );

    const availableColors = new Set(
      allVariants
        .filter((variant) => {
          if (!variant || !variant.availableForSale) {
            return false;
          }

          return isVariantVisibleByActiveMetafield(variant);
        })
        .map((variant) => {
          const colorOption = (variant.selectedOptions || []).find((option) => option?.name && option?.value && option.name.toLowerCase().includes('color'));
          return colorOption?.value ? colorOption.value.trim().toLowerCase() : '';
        })
        .filter(Boolean)
    );

    if (!availableVisibleVariantIds.size && !availableColors.size) {
      return;
    }

    const swatchButtons = item.querySelectorAll('.product-card-color-option');
    swatchButtons.forEach((button) => {
      const variantId = button.getAttribute('data-variant-id');
      const colorKey = (button.getAttribute('data-color') || '').trim().toLowerCase();

      const isAvailableByVariant = !!variantId && availableVisibleVariantIds.has(String(variantId));
      const isAvailableByColor = !!colorKey && availableColors.has(colorKey);

      if (isAvailableByVariant || isAvailableByColor) {
        return;
      }

      const slide = button.closest('.product-card-color-slide');
      if (slide) {
        slide.remove();
      } else {
        button.remove();
      }
    });

    const remainingSwatches = item.querySelectorAll('.product-card-color-option');
    if (!remainingSwatches.length) {
      return;
    }

    const hasActive = Array.from(remainingSwatches).some((swatch) => swatch.classList.contains('active'));
    if (!hasActive) {
      remainingSwatches[0].classList.add('active');
    }
  }

  renderColors(allVariants, item) {

  const container = item.querySelector('#item-product-img');
  const containerHover = item.querySelector('#item-product-img-hover');

  if (!container || !containerHover) return;

  const requestedVariantId = item.dataset.requestedVariantId || '';

  const colors = new Map();
  const isVariantVisibleByActiveMetafield = (variant) => {
    const activeRaw = variant?.active?.value;
    if (activeRaw === null || activeRaw === undefined || activeRaw === '') return true;

    const normalized = String(activeRaw).trim().toLowerCase();
    if (normalized === 'false' || normalized === '0') return false;

    return normalized === 'true' || normalized === '1';
  };

  let requestedColor = null;

  for (const variant of allVariants) {

    const colorOpt = variant.selectedOptions.find(o => o?.name && o.name.toLowerCase() === 'color');
    if (!colorOpt) continue;

    const color = colorOpt.value;

    const urls =
      variant.images?.references?.edges
        ?.map(e => e.node?.image?.url)
        ?.filter(Boolean) ?? [];

    if (!colors.has(color)) {
      colors.set(color, {
        available: false,
        visible: false,
        images: []
      });
    }

    const meta = colors.get(color);

    if (variant.availableForSale) meta.available = true;
    if (isVariantVisibleByActiveMetafield(variant)) meta.visible = true;

    if (urls.length) meta.images = urls;

    // Track which colour the specific variant we asked for (Recently Viewed's
    // remembered variant, stamped as data-requested-variant-id in fetchCard)
    // belongs to, so it wins below even when sold out — otherwise the loop
    // further down just grabs whichever colour happens to be in stock first,
    // ignoring the exact variant/colour the customer actually looked at.
    if (requestedVariantId && !requestedColor) {
      const variantNumericId = String(variant.id || '').split('/').pop();
      if (variantNumericId === String(requestedVariantId)) {
        requestedColor = color;
      }
    }

  }

  let firstImage = null;
  let secondImage = null;

  const requestedMeta = requestedColor ? colors.get(requestedColor) : null;

  if (requestedMeta && requestedMeta.visible && requestedMeta.images.length) {
    firstImage = requestedMeta.images[0];
    secondImage = requestedMeta.images.length > 1 ? requestedMeta.images[1] : requestedMeta.images[0];
  } else {
    for (const meta of colors.values()) {

      if (!meta.available || !meta.visible) continue;

      if (!firstImage && meta.images.length) {
        firstImage = meta.images[0];

        if (meta.images.length > 1) {
          secondImage = meta.images[1];
        } else {
          secondImage = meta.images[0];
        }

        break;
      }
    }
  }

  if (!firstImage) return;

  const cardAlt = Utilities.cardImageAlt(item);

  const imgMain = document.createElement('img');
  imgMain.src = firstImage;
  imgMain.alt = cardAlt;
  imgMain.width = "296";
  imgMain.height = "444";
  imgMain.loading = 'lazy';
  imgMain.decoding = 'async';
  container.appendChild(imgMain);

  const imgHover = document.createElement('img');
  imgHover.src = secondImage;
  imgHover.alt = cardAlt;
  // the hover shot is the same product from another angle, so keep it out of the reading order
  imgHover.setAttribute('aria-hidden', 'true');
  imgHover.width = "296";
  imgHover.height = "444";
  imgHover.loading = 'lazy';
  imgHover.decoding = 'async';
  containerHover.appendChild(imgHover);


}

  initSwiper() {
    if (typeof window.Swiper !== 'function') {
      return;
    }

    const swiperEl = this.querySelector('.recently-viewed-swiper');
    if (!swiperEl) {
      return;
    }

    const paginationEl = window.innerWidth < 768
      ? this.querySelector('.recently-viewed-pagination--mobile')
      : this.querySelector('.recently-viewed-pagination--desktop');

    new window.Swiper(swiperEl, {
      slidesPerView: 'auto',
      slidesPerGroup: 1,
      spaceBetween: 16,
      pagination: {
        el: paginationEl,
        clickable: true,
        dynamicBullets: true
      },
      breakpoints: {
        768: {
          slidesPerView: 4,
          slidesPerGroup: 1
        },
        992: {
          slidesPerView: 4,
          slidesPerGroup: 4,
          spaceBetween: 32,
          pagination: {
            el: paginationEl,
            clickable: true,
            dynamicBullets: false
          }
        }
      }
    });
  }

  toInteger(value) {
    const parsed = Number(value);
    if (!Number.isFinite(parsed)) {
      return 0;
    }
    return Math.round(parsed);
  }

  formatMoneyWithoutTrailingZeros(cents) {
    if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
      const moneyFormat = (window.theme && window.theme.moneyFormat) || window.moneyFormat || (window.Shopify && window.Shopify.money_format) || '${{amount}}';

      const formatted = window.Shopify.formatMoney(cents, moneyFormat);
      if (typeof formatted !== 'string') {
        return (this.toInteger(cents) / 100).toFixed(2).replace(/\.00$/, '');
      }
      return formatted.replace(/([\.,]00)(?!\d)/, '');
    }

    return (cents / 100).toFixed(2).replace(/\.00$/, '');
  }

  getSalePriceStyleAttribute() {
    const salePriceColor = (this.dataset.salePriceColor || '').trim();
    if (!salePriceColor) {
      return '';
    }

    return ' style="--sale-price-color: ' + salePriceColor + ';"';
  }

  renderCardPrice(priceCents, compareAtCents) {
    const safePrice = this.toInteger(priceCents);
    const safeCompareAt = this.toInteger(compareAtCents);
    const salePriceStyle = this.getSalePriceStyleAttribute();

    if (safeCompareAt > safePrice) {
      return '' +
        '<span class="money compare-price" style="text-decoration: line-through; padding-right: 5px;">' + this.formatMoneyWithoutTrailingZeros(safeCompareAt) + '</span>' +
        '<span class="money price sale-price"' + salePriceStyle + '>' + this.formatMoneyWithoutTrailingZeros(safePrice) + '</span>';
    }

    return '<span class="money price">' + this.formatMoneyWithoutTrailingZeros(safePrice) + '</span>';
  }

  initRecommendationCardPrices(scope) {
    const root = scope && scope.querySelectorAll ? scope : this;
    const priceContainers = root.querySelectorAll('[data-price-container][data-price-min]');

    priceContainers.forEach((el) => {
      if (el.children.length > 0) {
        return;
      }

      const priceMin = parseInt(el.getAttribute('data-price-min'), 10) || 0;
      const priceMax = parseInt(el.getAttribute('data-price-max'), 10) || 0;
      const compareAt = parseInt(el.getAttribute('data-compare-at'), 10) || 0;
      const showRange = el.getAttribute('data-show-range') === 'true';
      const hideRec = el.getAttribute('data-hide-rec') === 'true';
      const allOnSale = el.getAttribute('data-all-on-sale') === 'true';
      const saleColor = el.getAttribute('data-sale-color') || this.dataset.salePriceColor || '';
      const salePriceStyle = saleColor ? ' style="--sale-price-color: ' + saleColor + ';"' : '';

      let html = '';

      if (hideRec && !showRange) {
        html = '<span class="money price">' + this.formatMoneyWithoutTrailingZeros(priceMin) + '</span>';
      } else if (priceMin < priceMax && showRange) {
        if (compareAt > priceMax) {
          html =
            '<span class="sale-price"' + salePriceStyle + '>' +
              '<span class="money">' + this.formatMoneyWithoutTrailingZeros(priceMin) + '</span>' +
              '<span class="separator"> - </span>' +
              '<span class="money">' + this.formatMoneyWithoutTrailingZeros(priceMax) + '</span>' +
            '</span>' +
            '<span class="money compare-price" style="text-decoration: line-through; padding-left: 5px;">' +
              this.formatMoneyWithoutTrailingZeros(compareAt) +
            '</span>';
        } else {
          html =
            '<span class="money price-min">' + this.formatMoneyWithoutTrailingZeros(priceMin) + '</span>' +
            '<span class="separator"> - </span>' +
            '<span class="money price-max">' + this.formatMoneyWithoutTrailingZeros(priceMax) + '</span>';
        }
      } else if (allOnSale && compareAt > priceMin) {
        html =
          '<span class="money compare-price" style="text-decoration: line-through; padding-right: 5px;">' +
            this.formatMoneyWithoutTrailingZeros(compareAt) +
          '</span>' +
          '<span class="money price-min sale-price"' + salePriceStyle + '>' +
            this.formatMoneyWithoutTrailingZeros(priceMin) +
          '</span>';
      } else {
        html = '<span class="money price">' + this.formatMoneyWithoutTrailingZeros(priceMin) + '</span>';
      }

      el.innerHTML = html;
    });
  }

  initCardColorSwipers(scope) {
    if (typeof window.Swiper !== 'function') {
      return;
    }

    const root = scope && scope.querySelectorAll ? scope : document;
    const colorSliders = root.querySelectorAll('.product-card-colors');

    colorSliders.forEach((slider) => {
      if (slider.dataset.swiperReady === 'true') {
        return;
      }

      const track = slider.querySelector('.product-card-colors-track');
      const scrollbar = slider.querySelector('.product-card-colors-scrollbar');
      if (!track || !scrollbar) {
        return;
      }

      const slideCount = track.querySelectorAll('.product-card-color-slide').length;
      if (slideCount <= 1) {
        scrollbar.style.display = 'none';
        slider.dataset.swiperReady = 'true';
        return;
      }

      const swiperInstance = new window.Swiper(slider, {
        wrapperClass: 'product-card-colors-track',
        slideClass: 'product-card-color-slide',
        slidesPerView: 'auto',
        spaceBetween: 7,
        freeMode: {
          enabled: true,
          momentum: true,
          momentumRatio: 1,
          momentumVelocityRatio: 1,
          sticky: false
        },
        mousewheel: {
          enabled: true,
          forceToAxis: false,
          releaseOnEdges: true,
          sensitivity: 1,
          eventsTarget: 'container'
        },
        watchOverflow: true,
        scrollbar: {
          el: scrollbar,
          draggable: true,
          hide: false
        },
        observer: true,
        observeParents: true
      });

      slider.dataset.swiperReady = 'true';
      slider.__productCardColorsSwiper = swiperInstance;
    });
  }

  bindCardColorSwatchEvents() {
    if (window.__PRODUCT_CARD_COLOR_OPTIONS_RECENTLY_VIEWED_BOUND__) {
      return;
    }

    window.__PRODUCT_CARD_COLOR_OPTIONS_RECENTLY_VIEWED_BOUND__ = true;

    document.addEventListener('click', (event) => {
      const swatchButton = event.target.closest('recently-viewed-products .product-card-color-option');
      if (!swatchButton) {
        return;
      }

      const card = swatchButton.closest('.item-product');
      if (!card) {
        return;
      }

      const allSwatches = card.querySelectorAll('.product-card-color-option');
      allSwatches.forEach((button) => {
        button.classList.remove('active');
      });
      swatchButton.classList.add('active');

      const variantImage = swatchButton.getAttribute('data-image');
      if (variantImage) {
        const mainImage = card.querySelector('#item-product-img img');
        if (mainImage) {
          mainImage.setAttribute('src', variantImage);
          if (mainImage.getAttribute('data-src')) {
            mainImage.setAttribute('data-src', variantImage);
          }
          if (mainImage.getAttribute('data-lazy')) {
            mainImage.setAttribute('data-lazy', variantImage);
          }
        }

        const hoverImage = card.querySelector('#item-product-img-hover img');
        if (hoverImage) {
          hoverImage.setAttribute('src', variantImage);
          if (hoverImage.getAttribute('data-src')) {
            hoverImage.setAttribute('data-src', variantImage);
          }
          if (hoverImage.getAttribute('data-lazy')) {
            hoverImage.setAttribute('data-lazy', variantImage);
          }
        }
      }

      const priceContainer = card.querySelector('[data-price-container]');
      if (priceContainer) {
        const selectedPrice = swatchButton.getAttribute('data-price');
        const selectedCompareAtPrice = swatchButton.getAttribute('data-compare-at-price');
        priceContainer.innerHTML = this.renderCardPrice(selectedPrice, selectedCompareAtPrice);
      }

      const variantUrl = swatchButton.getAttribute('data-variant-url');
      if (variantUrl) {
        const productLinks = card.querySelectorAll('.product-card-link');
        productLinks.forEach((link) => {
          link.setAttribute('href', variantUrl);
        });
      }

      const selectedVariantId = swatchButton.getAttribute('data-variant-id');
      if (selectedVariantId) {
        card.setAttribute('data-current-variant-id', selectedVariantId);

        const quickAddScopes = card.querySelectorAll('.item-quick-add, .item-quick-add__button');
        quickAddScopes.forEach((scope) => {
          scope.setAttribute('data-current-variant-id', selectedVariantId);
        });
      }
    });
  }

}

customElements.define('recently-viewed-products', RecentlyViewedProducts);
